import { Controller, ControllerProps, FieldError, Validate, useFormContext } from 'react-hook-form';

import { CheckboxProps, FormControlLabel, FormHelperText, Stack } from '@mui/material';

import Checkbox from '@/components/Checkbox';

export interface FormCheckboxProps<T = boolean> extends Omit<CheckboxProps, 'name'> {
  name: ControllerProps['name'];
  label?: string | React.ReactElement;
  validate?: Validate<T, unknown> | Record<string, Validate<T, unknown>>;
}

const getNestedError = (name: string, errors: Record<string, unknown>): FieldError | undefined => {
  const result = name
    .split(/[.[\]]+/)
    .filter(Boolean)
    .reduce<unknown>((acc, key) => (acc as Record<string, unknown>)?.[key], errors);

  return result as FieldError | undefined;
};

function FormCheckbox<T = boolean>({ name, label, validate, ...props }: FormCheckboxProps<T>): React.ReactElement {
  const { control } = useFormContext();

  const render: ControllerProps['render'] = p => {
    const error = getNestedError(name, p.formState.errors as Record<string, unknown>) as FieldError | undefined;
    const hasError = Boolean(error);

    return (
      <Stack>
        <FormControlLabel control={<Checkbox {...props} {...p.field} checked={p.field.value} />} label={label} />
        {hasError && <FormHelperText error>{error?.message}</FormHelperText>}
      </Stack>
    );
  };

  return <Controller name={name} control={control} rules={{ validate }} render={render} />;
}

export default FormCheckbox;
